import React from 'react';
import ConfigSectionAccordion from './ConfigSectionAccordion';
import ConfigFieldRow from './ConfigFieldRow';
import ResolveControlRenderer from './ResolveControlRenderer';
import { useResolveConfig } from '../context/ResolveConfigContext';
import { ManifestSection, ManifestSetting } from '../manifest/types';
import { dependsOnSatisfied, effectiveValueOf } from '../manifest/valueUtils';

interface ResolveGenericSectionProps {
  section: ManifestSection;
}

/**
 * One Configuration section, rendered entirely from the schema the backend returned for
 * it — no setting name is ever hardcoded here. Replaces the five per-section components
 * (`IdentityDataSourcesSection`, `BrandingThemeSection`, `ConversationFeaturesSection`,
 * `AssistantToolsSection`, `ModelPromptSection`) that used to hand-wire each field.
 *
 * There is no admin-only settings tier (removed 2026-09-24) — every setting the backend
 * returns is merchant-visible, so this renders `section.settings` alone. See
 * `okf/resolve/e2e.md`.
 */
const ResolveGenericSection: React.FC<ResolveGenericSectionProps> = ({ section }) => {
  const { overrides, setOverride, errors, saving, settingsByName } = useResolveConfig();

  const renderRow = (setting: ManifestSetting) => {
    // A row in error blocks Save, so it stays on screen even while its `depends_on` would
    // hide it — otherwise "Fix the highlighted fields" points at nothing.
    if (!dependsOnSatisfied(setting, overrides, settingsByName) && !errors[setting.name]) return null;
    const value = effectiveValueOf(setting, overrides);
    return (
      <ConfigFieldRow
        key={setting.name}
        testId={`resolve-config-row-${setting.name}`}
        label={setting.title}
        description={setting.description}
        required={Boolean(setting.required)}
      >
        <ResolveControlRenderer
          setting={setting}
          value={value}
          disabled={saving}
          error={errors[setting.name] ?? null}
          testId={`resolve-config-row-${setting.name}-control`}
          onChange={(next) => setOverride(setting.name, next)}
        />
      </ConfigFieldRow>
    );
  };

  const hasVisibleContent = section.settings.length > 0;
  if (!hasVisibleContent) return null;

  return (
    <ConfigSectionAccordion
      title={section.title}
      name={section.name}
      hasError={section.settings.some((setting) => Boolean(errors[setting.name]))}
    >
      {section.settings.map(renderRow)}
    </ConfigSectionAccordion>
  );
};

export default ResolveGenericSection;
